import _extends from '@babel/runtime/helpers/esm/extends';
import _objectWithoutProperties from '@babel/runtime/helpers/esm/objectWithoutProperties';
import React, { useMemo, useState } from 'react';
import { IconButton } from '@paypalcorp/pp-react-icon-button';
import { CloseIcon, InformationIcon, CheckmarkBackgroundIcon, WarningIcon, CriticalIcon } from '@paypalcorp/pp-react-icons';
import { css, useTheme, jsx } from '@emotion/react';
import _taggedTemplateLiteral from '@babel/runtime/helpers/esm/taggedTemplateLiteral';
import { math } from 'polished';
import { textStyles } from '@paypalcorp/pp-react-text';
import { devWarning, getColorFamily, ColorProvider } from '@paypalcorp/pp-react-utils';
import _defineProperty from '@babel/runtime/helpers/esm/defineProperty';
import _slicedToArray from '@babel/runtime/helpers/esm/slicedToArray';

var _templateObject, _templateObject2, _templateObject3, _templateObject4, _templateObject5, _templateObject6, _templateObject7, _templateObject8;
function styles(tokens) {
  /* istanbul ignore next */
  if (!Object.keys(tokens).length) {
    devWarning('Missing Emotion theme. Use ThemeProvider from @emotion/react to pass a valid theme from pp-react.');
    return {};
  }

  var sysSpaceXs = tokens.sysSpaceXs,
      sysSpaceSm = tokens.sysSpaceSm,
      sysSpaceMd = tokens.sysSpaceMd,
      sysSpaceLg = tokens.sysSpaceLg,
      sysSizeFieldSm = tokens.sysSizeFieldSm,
      sysSizeIconSm = tokens.sysSizeIconSm,
      sysTypographyBodySize = tokens.sysTypographyBodySize,
      sysBorderThicknessNormal = tokens.sysBorderThicknessNormal,
      sysBorderRadiusLg = tokens.sysBorderRadiusLg,
      sysColorSystemMain = tokens.sysColorSystemMain,
      sysColorSystemMainActive = tokens.sysColorSystemMainActive,
      sysColorInfoMain = tokens.sysColorInfoMain,
      sysColorInfoContrast = tokens.sysColorInfoContrast,
      sysColorSuccessMain = tokens.sysColorSuccessMain,
      sysColorSuccessContrast = tokens.sysColorSuccessContrast,
      sysColorWarningMain = tokens.sysColorWarningMain,
      sysColorWarningContrast = tokens.sysColorWarningContrast,
      sysColorErrorMain = tokens.sysColorErrorMain,
      sysColorErrorContrast = tokens.sysColorErrorContrast;

  var _textStyles = textStyles(tokens),
      textBody = _textStyles.body;

  var alertPaddingRightSpacing = math("".concat(sysSpaceXs, " * 2"));
  var alertPaddingRight = math(" ".concat(alertPaddingRightSpacing, " + ").concat(sysSizeFieldSm));
  var alertPaddingLeft = math("".concat(sysSpaceLg, " + ").concat(sysTypographyBodySize, " + ").concat(sysSpaceMd));
  var alertPaddingTop = math("".concat(sysSpaceLg, " - ").concat(sysBorderThicknessNormal));
  var alertPaddingBottom = math("".concat(sysSpaceLg, " - ").concat(sysBorderThicknessNormal));
  var alertPaddingRightWithoutClose = math("".concat(sysSpaceLg, " - ").concat(sysBorderThicknessNormal));
  var alertIconSpaceLeft = math("".concat(sysSpaceLg, "- ").concat(sysBorderThicknessNormal));
  var sysAlertCloseBtnSpaceTop = math("".concat(sysSpaceSm, " - ").concat(sysBorderThicknessNormal));
  var sysAlertCloseBtnSpaceRight = math("".concat(sysSpaceSm, " - ").concat(sysBorderThicknessNormal));
  var sysAlertIconSvgSpaceTop = math("".concat(sysSpaceLg, "- ").concat(sysBorderThicknessNormal));
  var base = css(_templateObject || (_templateObject = _taggedTemplateLiteral(["\n    label: alert_base;\n    box-sizing: border-box;\n    padding: ", " ", " ", "\n      ", ";\n    border-width: ", ";\n    border-style: solid;\n    border-radius: ", ";\n    position: relative;\n    text-align: initial;\n    ", ";\n\n    [dir='rtl'] & {\n      padding-left: ", ";\n      padding-right: ", ";\n\n      &:before {\n        left: auto;\n        right: ", ";\n      }\n    }\n  "])), alertPaddingTop, alertPaddingRight, alertPaddingBottom, alertPaddingLeft, sysBorderThicknessNormal, sysBorderRadiusLg, textBody({
    color: sysColorSystemMain
  }), alertPaddingRight, alertPaddingLeft, alertIconSpaceLeft);
  var svgIcon = css(_templateObject2 || (_templateObject2 = _taggedTemplateLiteral(["\n    label: svg_icon;\n    display: inline-block;\n    position: absolute;\n    left: ", ";\n    text-align: center;\n    color: ", ";\n    line-height: ", ";\n    font-size: ", ";\n    top: ", ";\n    vertical-align: middle;\n\n    [dir='rtl'] & {\n      left: auto;\n      right: ", ";\n    }\n  "])), alertIconSpaceLeft, sysColorSystemMainActive, sysSizeIconSm, sysSizeIconSm, sysAlertIconSvgSpaceTop, alertIconSpaceLeft);
  var closeBtn = css(_templateObject3 || (_templateObject3 = _taggedTemplateLiteral(["\n    label: close_btn;\n    position: absolute;\n    top: ", ";\n    right: ", ";\n\n    [dir='rtl'] & {\n      left: ", ";\n      right: auto;\n    }\n  "])), sysAlertCloseBtnSpaceTop, sysAlertCloseBtnSpaceRight, sysAlertCloseBtnSpaceRight);

  var alertColor = function (backgroundColor, color) {
    return css(_templateObject4 || (_templateObject4 = _taggedTemplateLiteral(["\n      border: none;\n      background-color: ", ";\n      color: ", ";\n    "])), backgroundColor, color);
  };

  var alertType = function (type) {
    var colorMap = {
      info: alertColor(sysColorInfoMain, sysColorInfoContrast),
      warning: alertColor(sysColorWarningMain, sysColorWarningContrast),
      success: alertColor(sysColorSuccessMain, sysColorSuccessContrast),
      error: alertColor(sysColorErrorMain, sysColorErrorContrast)
    };
    return css(_templateObject5 || (_templateObject5 = _taggedTemplateLiteral(["\n      label: ", ";\n      ", ";\n    "])), type, colorMap[type]);
  };

  var iconColor = function (color) {
    return css(_templateObject6 || (_templateObject6 = _taggedTemplateLiteral(["\n      color: ", ";\n    "])), color);
  };

  var alertIcon = function (type) {
    var iconMap = {
      info: iconColor(sysColorInfoContrast),
      warning: iconColor(sysColorWarningContrast),
      success: iconColor(sysColorSuccessContrast),
      error: iconColor(sysColorErrorContrast)
    };
    var classLabel = "".concat(type, "_icon");
    return css(_templateObject7 || (_templateObject7 = _taggedTemplateLiteral(["\n      label: ", ";\n      ", ";\n    "])), classLabel, iconMap[type]);
  };

  var noCloseBtn = css(_templateObject8 || (_templateObject8 = _taggedTemplateLiteral(["\n    label: no_close_btn;\n    padding-right: ", ";\n    [dir='rtl'] & {\n      padding-left: ", ";\n      padding-right: ", ";\n    }\n  "])), alertPaddingRightWithoutClose, alertPaddingRightWithoutClose, alertPaddingLeft);
  return {
    base: base,
    svgIcon: svgIcon,
    closeBtn: closeBtn,
    noCloseBtn: noCloseBtn,
    alertType: alertType,
    alertIcon: alertIcon
  };
}

var _excluded = ["className", "type", "typeIconAriaLabel", "closeButton", "closeButtonLabel", "children"];
var icons = {
  info: InformationIcon,
  success: CheckmarkBackgroundIcon,
  warning: WarningIcon,
  error: CriticalIcon
};

var Alert = function (_ref) {
  var _styles$alertType, _styles$alertIcon;

  var className = _ref.className,
      _ref$type = _ref.type,
      type = _ref$type === void 0 ? 'info' : _ref$type,
      _ref$typeIconAriaLabe = _ref.typeIconAriaLabel,
      typeIconAriaLabel = _ref$typeIconAriaLabe === void 0 ? type : _ref$typeIconAriaLabe,
      closeButton = _ref.closeButton,
      _ref$closeButtonLabel = _ref.closeButtonLabel,
      closeButtonLabel = _ref$closeButtonLabel === void 0 ? 'close alert' : _ref$closeButtonLabel,
      children = _ref.children,
      rest = _objectWithoutProperties(_ref, _excluded);

  var tokens = useTheme();

  var _styles = useMemo(function () {
    return styles(tokens);
  }, [tokens]);

  var AlertStatusIcon = icons[type];
  var alertCSSVal = [_styles.base, (_styles$alertType = _styles.alertType) === null || _styles$alertType === void 0 ? void 0 : _styles$alertType.call(_styles, type), !closeButton && _styles.noCloseBtn];
  var closeBtnEl;

  if (closeButton) {
    var otherProps = _extends({}, closeButton);

    var alertCloseBtnCSSVal = [_styles.closeBtn];
    closeBtnEl = closeButton && jsx("span", {
      "data-ppui": "true"
    }, jsx(IconButton, _extends({
      css: alertCloseBtnCSSVal,
      buttonType: "tertiary",
      size: "sm",
      svgIconComponent: CloseIcon
    }, otherProps, {
      "data-ppui": "true"
    }), closeButtonLabel));
  }

  var alertSvgCSSVal = [_styles.svgIcon, (_styles$alertIcon = _styles.alertIcon) === null || _styles$alertIcon === void 0 ? void 0 : _styles$alertIcon.call(_styles, type)];
  var alert = jsx("div", _extends({
    role: "alert",
    css: alertCSSVal,
    className: className,
    "data-ppui-info": "alert_3.4.6"
  }, rest), jsx(AlertStatusIcon, {
    size: "sm",
    css: alertSvgCSSVal,
    role: "img",
    "aria-label": typeIconAriaLabel,
    "data-ppui": "true"
  }), children, closeBtnEl);
  var colorFamily = getColorFamily({
    info: 'Info',
    warning: 'Warning',
    success: 'Success',
    error: 'Error'
  }[type], tokens);
  return jsx(ColorProvider, {
    colorFamily: colorFamily,
    "data-ppui": "true"
  }, alert);
};

var _excluded$1 = ["className", "type", "typeIconAriaLabel", "closeButton", "closeButtonLabel", "children"];

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

var DismissibleAlert = function (_ref) {
  var className = _ref.className,
      _ref$type = _ref.type,
      type = _ref$type === void 0 ? 'info' : _ref$type,
      _ref$typeIconAriaLabe = _ref.typeIconAriaLabel,
      typeIconAriaLabel = _ref$typeIconAriaLabe === void 0 ? type : _ref$typeIconAriaLabe,
      _ref$closeButton = _ref.closeButton,
      closeButton = _ref$closeButton === void 0 ? {} : _ref$closeButton,
      _ref$closeButtonLabel = _ref.closeButtonLabel,
      closeButtonLabel = _ref$closeButtonLabel === void 0 ? 'close alert' : _ref$closeButtonLabel,
      children = _ref.children,
      rest = _objectWithoutProperties(_ref, _excluded$1);

  var _useState = useState(true),
      _useState2 = _slicedToArray(_useState, 2),
      showAlert = _useState2[0],
      setShowAlert = _useState2[1];

  var closeBtnProps = _objectSpread({}, closeButton);

  closeBtnProps.onClick = function (event) {
    if (closeButton && closeButton.onClick) {
      closeButton.onClick(event);
    }

    setShowAlert(false);
  };

  return showAlert && /*#__PURE__*/React.createElement(Alert, _extends({
    className: className,
    type: type,
    typeIconAriaLabel: typeIconAriaLabel,
    closeButton: closeBtnProps,
    closeButtonLabel: closeButtonLabel
  }, rest, {
    "data-ppui": "true"
  }), children);
};

export { Alert, DismissibleAlert, styles as alertStyles };
